// src/pages/Admin.jsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "../lib/supabaseClient";
import logo from "../assets/images/newlogo.jpg";

import "./admin.css";
import AdminHeader from "../components/Admin/AdminHeader";


function Admin() {

  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // General UI state
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);


  /*
   * ---------------------------------------------------------
   * CHECK INITIAL SESSION
   * ---------------------------------------------------------
   */

  useEffect(() => {

    const getInitialSession = async () => {

      const { data, error } =
        await supabase.auth.getSession();

      if (error) {
        setError(error.message);
      }

      setSession(data.session);
      setCheckingSession(false);
    };


    getInitialSession();


    /*
     * Listen for authentication changes.
     */

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {

        setSession(currentSession);
        setCheckingSession(false);

      }
    );


    return () => {
      subscription.unsubscribe();
    };

  }, []);


  /*
   * ---------------------------------------------------------
   * LOGIN
   * ---------------------------------------------------------
   */

  const handleLogin = async (e) => {

    e.preventDefault();

    setError("");
    setLoading(true);


    const { data, error } =
      await supabase.auth.signInWithPassword({

        email,
        password,

      });


    if (error) {

      setError(error.message);
      setLoading(false);

      return;
    }


    setSession(data.session);

    setLoading(false);
  };


  /*
   * ---------------------------------------------------------
   * LOGOUT
   * ---------------------------------------------------------
   */

  const handleLogout = async () => {

    setError("");

    const { error } =
      await supabase.auth.signOut();


    if (error) {

      setError(error.message);

      return;
    }

    setSession(null);
  };


  /*
   * ---------------------------------------------------------
   * CHECKING SESSION
   * ---------------------------------------------------------
   */

  if (checkingSession) {

    return (
      <div className="admin-page">

        <div className="admin-loading-screen">

          <div className="admin-spinner"></div>

          <p>
            Checking login...
          </p>

        </div>

      </div>
    );
  }


  /*
   * ---------------------------------------------------------
   * LOGIN SCREEN
   * ---------------------------------------------------------
   */

  if (!session) {

    return (
      <div className="admin-page">

        <div className="admin-login-wrapper">

          <form
            className="admin-login-card"
            onSubmit={handleLogin}
          >

            <div className="admin-login-icon">

              <img
                src={logo}
                alt="Sudhamshu Ayurveda"
              />

            </div>


            <h1>
              Admin Login
            </h1>


            <p className="admin-subtitle">
              Sudhamshu Ayurveda
            </p>


            <div className="admin-title-divider"></div>


            <div className="admin-form">

              <div className="admin-form-group">

                <label htmlFor="email">
                  Email
                </label>

                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  required
                  autoComplete="email"
                  placeholder="Enter your email"
                />

              </div>


              <div className="admin-form-group">

                <label htmlFor="password">
                  Password
                </label>

                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />

              </div>


              {error && (

                <div className="admin-error">
                  {error}
                </div>

              )}


              <button
                type="submit"
                disabled={loading}
                className="admin-primary-btn"
              >

                {loading
                  ? "Logging in..."
                  : "Login"}

              </button>

            </div>

          </form>

        </div>

      </div>
    );
  }


  /*
   * ---------------------------------------------------------
   * ADMIN DASHBOARD
   * ---------------------------------------------------------
   */

  return (

    <div className="admin-page">

      <div className="admin-dashboard">


        {/* =================================================
            HEADER
            ================================================= */}

        <AdminHeader
          showLogout
          onLogout={handleLogout}
        />


        {/* =================================================
            DASHBOARD
            ================================================= */}

        <main className="admin-main">


          <section className="admin-title-section">

            <h1>
              Administration
            </h1>

            <div className="admin-title-divider"></div>

            <p>
              Manage your website content and services.
            </p>

          </section>


          {/* =================================================
              ADMIN FEATURES
              ================================================= */}

          <section className="admin-feature-grid">


            {/* ---------------------------------------------
                SWARNAPRASHANA
                --------------------------------------------- */}

            <Link
              to="/admin/swarnaprashana"
              className="admin-feature-card"
            >

              <div className="admin-feature-icon">
                📅
              </div>

              <div className="admin-feature-content">

                <h2>
                  Swarnaprashana
                </h2>

                <p>
                  Manage upcoming Swarnaprashana
                  dates and schedule.
                </p>

                <span className="admin-feature-link">
                  Manage schedule →
                </span>

              </div>

            </Link>


            {/* ---------------------------------------------
                JOURNAL
                --------------------------------------------- */}

            <Link
              to="/admin/journal"
              className="admin-feature-card"
            >

              <div className="admin-feature-icon">
                ✎
              </div>

              <div className="admin-feature-content">

                <h2>
                  Journal
                </h2>

                <p>
                  Create, edit and publish Ayurvedic
                  articles.
                </p>

                <span className="admin-feature-link">
                  Manage articles →
                </span>

              </div>

            </Link>


            {/* ---------------------------------------------
                ACTIVITIES
                --------------------------------------------- */}

            <Link
              to="/admin/activities"
              className="admin-feature-card"
            >

              <div className="admin-feature-icon">
                📷
              </div>

              <div className="admin-feature-content">

                <h2>
                  Activities
                </h2>

                <p>
                  Health camps, awareness programs,
                  seminars and practice activities.
                </p>

                <span className="admin-feature-link">
                  Manage activities →
                </span>

              </div>

            </Link>


            {/* ---------------------------------------------
                HOMEPAGE
                --------------------------------------------- */}

            <Link
              to="/admin/homepage"
              className="admin-feature-card"
            >

              <div className="admin-feature-icon">
                🏠
              </div>

              <div className="admin-feature-content">

                <h2>
                  Homepage
                </h2>

                <p>
                  Coming Soon ........
                </p>

                <span className="admin-feature-link">
                  Manage homepage →
                </span>

              </div>

            </Link>


          </section>


        </main>

      </div>

    </div>

  );
}


export default Admin;